import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react"
import { DateTime } from "luxon"

// Styles
import formStyles from "../../../../styles/Form-Inputs.module.css"
import myStyles from "../../../../styles/referrals/ReferralEntityList.module.css"

// Context
import { UserContext } from "../../../../context/userContext"

// Prime React
import { Tooltip } from "primereact/tooltip"
import { Dialog } from "primereact/dialog"
import { ListBox } from "primereact/listbox"

//Referral API
import {
  referralsApiReferralEntitiesPost,
  refApiServiceCategoriesGet,
  referralsApiReferralLocationsMappedPost,
} from "../../partnerAPI"

//Referal modules
import RefGetErrorMessage from "../../../Referrals/Components/RefGetErrorMessage"
import { Accordion, AccordionTab } from "primereact/accordion"
import ReferralEntitiesListFilter from "./ReferralEntitiesListFilter"
import ReferralEntityFormDialog from "./Form/ReferralEntityFormDialog"
import { useLocation } from "react-router-dom"
import {
  getBoundingBox,
  getPartnerLocationFeaturePoints,
  getServiceCategoryNode,
  mapBox_Places_GetCoordinates,
} from "../sharedFunctions"

//GraphQL
import {
  GET_USERS_FROM_LIST,
  GET_ORGANIZATIONS,
  GET_ORGANIZATION_ADDRESS,
} from "../queries"
import { useApolloClient, useQuery } from "@apollo/client"
import { LogError } from "../../../../helpers/logger"
import ReferralEntitiesListTable from "./ReferralEntitiesListTable"
import ReferralEntitiesListHybridView from "./ReferralEntitiesListHybridView"

export default function EntitiesList() {
  const userCtx = useContext(UserContext)
  const location = useLocation()

  //Note: Use the apollo client directly to query instead of useLazyQuery
  //      If the session object is updated to reflect a new organization
  //      name, useLazyQuery currently causes an infinite loop on await
  //      This seems to be related to the Apollo client being recreated on userCtx.session object refresh
  const client = useApolloClient()

  const isPartnerUser =
    userCtx.session.user.staff_role === 9 ||
    userCtx.session.user.staff_role === 10
  const defaultPartnerUser = "-7"

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMapData, setIsLoadingMapData] = useState(true)
  const [errorMessage, setErrorMessage] = useState("")
  const [refEntityData, setEntityData] = useState()
  const [viewStyle, setViewStyle] = useState("List")

  const [serviceDialogTitle, setServiceDialogTitle] = useState("")
  const [serviceDialogServices, setServiceDialogServices] = useState([])

  //Mapping variables
  const [mapPartnerLocations, setMapPartnerLocations] = useState([])
  const [mapBoundingBox, setMapBoundingBox] = useState()
  const [mapCachedMap, setMapCachedMap] = useState()
  const [mapCenteredPoint, setMapCenteredPoint] = useState({
    longitude: -77,
    latitude: 38,
  })
  const [mapMarkerPoints, setMapMarkerPoints] = useState({})
  const [mapHeight, setMapHeight] = useState(648)
  const mainBodyRef = useRef()

  //Filters & pagination
  const [filters, setFilters] = useState({
    skipDataLoad: true,
    searchPhrase: "",
    createdFrom: undefined,
    createdTo: undefined,
    skipPagination: false,
    sortField: "name",
    sortAscending: true,
    allLocations: true,
    address: "",
    distance: 30,
    longitude: -77,
    latitude: 38,
    pageSize: 25,
    PageNum: 1,
    userStaffRole: userCtx.session.user.staff_role,
  })

  const [loadListOnly, setLoadListOnly] = useState(false)

  const [cityOptions, setCityOptions] = useState([])
  const [zipOptions, setZipOptions] = useState([])
  const [creatingOrganizations, setCreatingOrganizations] = useState([])
  const [creatingStaff, setCreatingStaff] = useState([])
  const [allOrganizations, setAllOrganizations] = useState(undefined)
  const [CategoryOptions, setCategoryOptions] = useState(undefined)

  //Referral entity form
  const [editPartnerId, setEditPartnerId] = useState(undefined)
  const [showChangeRequests, setShowChangeRequests] = useState(false)
  const [showPartnerForm, setShowPartnerForm] = useState(false)

  //GraphQL queres
  useQuery(GET_ORGANIZATION_ADDRESS, {
    variables: {
      uid: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    fetchPolicy: "network-only",
    onCompleted: async (result) => {
      try {
        let address = `${
          !!result.organization.organization_address
            ? result.organization.organization_address + ","
            : ""
        } ${
          !!result.organization.organization_city
            ? result.organization.organization_city + ","
            : ""
        } ${
          !!result.organization.organization_state
            ? result.organization.organization_state + ","
            : ""
        } ${
          !!result.organization.organization_country
            ? result.organization.organization_country + ","
            : ""
        } ${
          !!result.organization.organization_zip_code
            ? result.organization.organization_zip_code
            : ""
        }`
          .trim()
          .replace(/,\s*$/, "")

        const geoLocation = await mapBox_Places_GetCoordinates(address, userCtx.mapBoxKey)
        let longitude = filters.longitude
        let latitude = filters.latitude
        if (geoLocation) {
          setMapCenteredPoint(geoLocation)
          longitude = geoLocation.longitude
          latitude = geoLocation.latitude
        }

        setFilters({
          ...filters,
          address: address,
          longitude: longitude,
          latitude: latitude,
          skipDataLoad: false,
        })
      } catch (ex) {
        setFilters({ ...filters, address: "", skipDataLoad: false })
      }
    },
    onError: (error) => {
      setFilters({ ...filters, address: "", skipDataLoad: false })
    },
  })

  useEffect(() => {
    if (userCtx.session.user.staff_role !== filters.userStaffRole) {
      filters.userStaffRole = userCtx.session.user.staff_role
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.user.staff_role])

  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      // Depending on the layout, you may need to swap inlineSize with blockSize
      // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry/contentBoxSize
      setMapHeight(event[0].contentBoxSize[0].blockSize)
    })

    if (mainBodyRef) {
      resizeObserver.observe(mainBodyRef.current)
    }
  }, [mainBodyRef])

  useEffect(() => {
    refApiServiceCategoriesGet(userCtx.token)
      .then((result) => {
        let nodes = result.map((cat) => getServiceCategoryNode(cat))
        setCategoryOptions(nodes)
      })
      .catch((error) => {
        LogError("Unable to load service categories", error)
        userCtx.setNotificationError("Unable to load service categories")
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //When a profile ID is specified in the location state, open the form for that partner
  useEffect(() => {
    if (!!location.state?.profileId) {
      setEditPartnerId(location.state.profileId)
      if (isPartnerUser) {
        setShowChangeRequests(true)
      }
      setShowPartnerForm(true)
      location.state.id = undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.state?.profileId])

  const loadPartners = useCallback(async () => {
    try {
      if (!filters.skipDataLoad) {
        setIsLoading(true)
        setErrorMessage("")
        const convertDateToIso = (date) => {
          const utcDate = DateTime.fromJSDate(date).toUTC().toISO()

          return utcDate
        }

        const useFilters = { ...filters }

        if (!!useFilters.createdFrom) {
          useFilters.createdFrom = convertDateToIso(useFilters.createdFrom)
        }

        if (!!useFilters.createdTo) {
          useFilters.createdTo = convertDateToIso(useFilters.createdTo)
        }

        if (!!useFilters.referralCategories) {
          let categoryIds = Object.keys(useFilters.referralCategories)

          if (categoryIds.length > 0) {
            categoryIds = categoryIds.filter(
              (cat) => useFilters.referralCategories[cat].checked
            )
          }

          useFilters.referralCategoryIds = categoryIds
        }

        //Load map data
        if (viewStyle === "Map" || viewStyle === "ListMap") {
          const mapFilters = {
            searchPhrase: useFilters.searchPhrase,
            allLocations: useFilters.allLocations,
            distance: useFilters.distance,
            centerLongitude: filters.longitude,
            centerLatitude: filters.latitude,
            referralCategories: useFilters.referralCategoryIds,
            organizationId: userCtx.session.user.organization_id,
          }

          if (!loadListOnly) {
            setIsLoadingMapData(true)
            referralsApiReferralLocationsMappedPost(userCtx.token, mapFilters)
              .then((result) => {
                setMapPartnerLocations(result)

                if (!mapFilters.allLocations && result?.length === 0) {
                  userCtx.setNotificationError(
                    "There are no partners available within the specified range"
                  )
                }

                const points = getPartnerLocationFeaturePoints(
                  result,
                  mapFilters.distance
                )
                setMapMarkerPoints(points)

                if (mapCenteredPoint && result?.locations?.length > 0) {
                  const ClosestDistance = Math.min(
                    ...result?.locations.map((location) => location.distance)
                  )
                  const ClosestLocations = result.locations.filter(
                    (location) => location.distance === ClosestDistance
                  )

                  const boundingBox = getBoundingBox(
                    ClosestLocations[0],
                    mapCenteredPoint
                  )
                  setMapBoundingBox({
                    mappedReferralEntity: ClosestLocations[0],
                    boundsBox: boundingBox,
                  })
                }

                setIsLoadingMapData(false)
              })
              .catch((error) => {
                LogError("Unable to load mapped referral locations", error)
                userCtx.setNotificationError(
                  "Unable to load mapped referral locations"
                )
                setMapPartnerLocations([])
                setIsLoadingMapData(false)
              })
          }
        }

        if (useFilters.allLocations || viewStyle === "List") {
          useFilters.distance = 0
        } else {
          useFilters.centerLongitude = filters.longitude
          useFilters.centerLatitude = filters.latitude
        }

        // 9 == partnerAdmin / 10 === partnerUser
        if (
          userCtx.session.user.staff_role === 9 ||
          userCtx.session.user.staff_role === 10
        ) {
          useFilters.entityOrganizationId = userCtx.session.user.organization_id
        } else {
          useFilters.entityOrganizationId = "0"
        }

        const result = await referralsApiReferralEntitiesPost(
          userCtx.token,
          useFilters
        )

        let userList = creatingStaff

        if (result.creatorUsers?.length > 0) {
          let newUsers = result.creatorUsers.filter(
            (creator) =>
              !creatingStaff.some((staff) => staff.staff_id === creator)
          )

          if (newUsers.length > 0) {
            const userListQuery = await client.query({
              query: GET_USERS_FROM_LIST,
              variables: {
                list: newUsers,
              },
            })

            if (!!userListQuery.data?.usersFromList) {
              let distinctUsersList = []

              userListQuery.data.usersFromList
                .filter((staff) => !!staff)
                .forEach((user) => {
                  if (
                    !distinctUsersList.some(
                      (userEntry) => userEntry.staff_id === user.staff_id
                    )
                  ) {
                    distinctUsersList = [...distinctUsersList, user]
                  }
                })

              userList = [...creatingStaff, ...distinctUsersList]
              setCreatingStaff(userList)
            }
          } else {
            userList = creatingStaff
          }
        }

        if (result.partnerCities?.length > 0) {
          setCityOptions([
            { name: "All cities", value: "" },
            ...result.partnerCities
              .filter((city) => !!city)
              .map((city) => {
                return { name: city, value: city }
              }),
          ])
        }

        if (result.partnerZipCodes?.length > 0) {
          setZipOptions([
            { name: "All zips", value: "" },
            ...result.partnerZipCodes
              .filter((zip) => !!zip)
              .map((zip) => {
                return { name: zip, value: zip }
              }),
          ])
        }

        let orgs = allOrganizations
        if (!orgs) {
          orgs = await client.query({
            query: GET_ORGANIZATIONS,
            variables: {
              staff_id: defaultPartnerUser,
            },
          })

          let organizationList = []

          if (!!orgs.data.organizationsByUid) {
            organizationList = [
              ...organizationList,
              ...orgs.data.organizationsByUid,
            ]
          }

          if (!!orgs.data.getAllEnterprise) {
            organizationList = [
              ...organizationList,
              ...orgs.data.getAllEnterprise,
            ]
          }

          setAllOrganizations(orgs.data.organizationsByUid)
          orgs = organizationList
        }

        if (result.creatorOrganizations?.length > 0) {
          let newOrganizations = result.creatorOrganizations?.filter(
            (organizationId) =>
              !!organizationId &&
              !creatingOrganizations.some(
                (org) => org.organization_id === organizationId
              )
          )

          if (newOrganizations.length > 0) {
            const newOrgProfiles = orgs.filter((org) =>
              newOrganizations.includes(org.organization_id)
            )

            setCreatingOrganizations([
              ...newOrgProfiles,
              ...creatingOrganizations,
            ])
          }
        }

        if (loadListOnly) {
          setLoadListOnly(false)
        }

        setEntityData(result)
        setIsLoading(false)
      }
    } catch (error) {
      setErrorMessage(RefGetErrorMessage(error))
      //setNotification({Error: error, Header: "Error during submit"})
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, viewStyle])

  //Load Referral entities for the interface
  useEffect(() => {
    loadPartners()
  }, [loadPartners])

  const onPageChange = (event) => {
    setLoadListOnly(true)
    setFilters({ ...filters, pageSize: event.rows, PageNum: event.page + 1 })
  }

  //Decide on routes:

  const FilterHeader = () => {
    return (
      <div className={myStyles.filterHeaderGridContainer}>
        <div className={myStyles.filterHeaderGridTitle}>Filters</div>
        <div className={myStyles.filterHeaderGridButton}>
          <Tooltip target=".filter-header-button" />
          <div className={myStyles.filterHeaderButtonGroup}>
            {!isPartnerUser ? (
              <button
                type="button"
                className={`btn ${myStyles.filterHeaderButtonAdd} filter-header-button`}
                onClick={(e) => {
                  e.stopPropagation()
                  setShowPartnerForm(true)
                }}
                disabled={isLoading}
                data-pr-tooltip="Add new parter"
                data-pr-position="bottom"
              >
                <span className={`material-icons`}> {"add"} </span>
              </button>
            ) : null}
            <button
              type="button"
              className={`btn ${myStyles.filterHeaderButtonRefresh} filter-header-button`}
              onClick={(e) => {
                e.stopPropagation()
                if (!filters.includeFilterListing) {
                  setFilters({ ...filters, includeFilterListing: true })
                } else {
                  loadPartners()
                }
              }}
              disabled={isLoading}
              data-pr-tooltip="Refresh partner list"
              data-pr-position="bottom"
            >
              <span className={`material-icons`}> {"refresh"} </span>
            </button>
          </div>
          <div className={myStyles.filterHeaderButtonGroupViews}>
            <button
              type="button"
              className={`btn  ${
                myStyles.filterHeaderButtonListView
              } filter-header-button ${
                viewStyle === "List"
                  ? myStyles.filterHeaderButtonSelectedView
                  : ""
              } ${myStyles.filterHeaderButton} `}
              onClick={(e) => {
                e.stopPropagation()
                setViewStyle("List")
              }}
              data-pr-tooltip="Show partners in list view"
              data-pr-position="bottom"
              disabled={isLoading}
            >
              <span className={`material-icons`}> {"menu"} </span>
            </button>
            <button
              type="button"
              className={`btn ${
                myStyles.filterHeaderButtonListMapView
              } filter-header-button ${
                viewStyle === "ListMap"
                  ? myStyles.filterHeaderButtonSelectedView
                  : ""
              } ${myStyles.filterHeaderButton}`}
              onClick={(e) => {
                e.stopPropagation()
                setViewStyle("ListMap")
              }}
              data-pr-tooltip="Show partners in a list and on a map"
              data-pr-position="bottom"
              disabled={isLoading}
            >
              <span className={`material-icons`}> {"vertical_split"} </span>
            </button>
            <button
              type="button"
              className={`btn ${
                myStyles.filterHeaderButtonMapView
              } filter-header-button ${
                viewStyle === "Map"
                  ? myStyles.filterHeaderButtonSelectedView
                  : ""
              } ${myStyles.filterHeaderButton}`}
              onClick={(e) => {
                e.stopPropagation()
                setViewStyle("Map")
              }}
              data-pr-tooltip="Show partners on a map"
              data-pr-position="bottom"
              disabled={isLoading}
            >
              <span className={`material-icons`}> {"language"} </span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  //NOTE: Filter data based on context
  return (
    <div className={myStyles.mainDiv}>
      <div className={myStyles.mainItemHeader}>
        <h1>{isPartnerUser ? "Referral partners" : "Referrals network"}</h1>
        {errorMessage !== "" ? (
          <div style={{ marginBottom: "10px" }}>
            <label className={formStyles.errFieldMsg}>{errorMessage}</label>
          </div>
        ) : null}
        <ReferralEntityFormDialog
          showDialog={showPartnerForm}
          onHideDialog={() => {
            setShowPartnerForm(false)
            setShowChangeRequests(false)
            setEditPartnerId(undefined)
          }}
          showChangeRequests={showChangeRequests}
          referralPartnerId={editPartnerId}
          onProfileUpdated={async (profile) => {
            if (
              userCtx.session.user.organization_id ===
                profile.referralEntity.organizationId &&
              userCtx.session.user.organization_name !==
                profile.referralEntity.name
            ) {
              await userCtx.changeOrganization(
                userCtx.session.user.organization_id
              )
            }
            setFilters({
              ...filters,
              refreshPartners: !!filters.refreshPartners,
            })

            setShowPartnerForm(false)
            setShowChangeRequests(false)
            setEditPartnerId(undefined)
          }}
          onSelectPartner={(partnerId) => {
            setShowPartnerForm(false)
            setShowChangeRequests(!!isPartnerUser)
            setEditPartnerId(partnerId)
            setShowPartnerForm(true)
          }}
        />
        <Dialog
          header={serviceDialogTitle}
          onHide={() => setServiceDialogTitle("")}
          visible={serviceDialogTitle !== ""}
          baseZIndex={1000}
        >
          <div style={{ minHeight: "300" }}>
            {!!serviceDialogServices && serviceDialogServices.length > 0 ? (
              <ListBox
                filter
                options={serviceDialogServices}
                optionLabel="value"
              />
            ) : (
              <>No services available</>
            )}
          </div>
        </Dialog>
        <Accordion>
          <AccordionTab
            header={<FilterHeader />}
          >
            <ReferralEntitiesListFilter
              activeFilters={filters}
              setActiveFilters={(newFilter) => {
                setFilters(newFilter)
              }}
              cityOptions={cityOptions}
              zipOptions={zipOptions}
              organizationOptions={creatingOrganizations}
              staffOptions={creatingStaff}
              referralCategories={CategoryOptions}
              isLoading={isLoading}
              showMapFilters={viewStyle !== "List"}
            />
          </AccordionTab>
        </Accordion>
      </div>
      <div className={myStyles.mainItemBody} ref={mainBodyRef}>
        {viewStyle === "List" ? (
          <ReferralEntitiesListTable
            partnerData={refEntityData}
            currentUserRole={userCtx.session.user.staff_role}
            currentUserOrganizationIds={
              userCtx.session?.organizations?.map(
                (organization) => organization.organization_id
              ) || []
            }
            isLoading={isLoading}
            isPartnerUser={isPartnerUser}
            filters={filters}
            onSetFilters={(newFilters) => {
              setFilters(newFilters)
            }}
            onShowPartnerServices={(partnerName, newServiceList) => {
              setServiceDialogServices(newServiceList)
              setServiceDialogTitle(`Services offered by ${partnerName}`)
            }}
            onEditPartner={(partnerId) => {
              setEditPartnerId(partnerId)
              setShowPartnerForm(true)
            }}
            onPageChange={onPageChange}
          />
        ) : null}
        {viewStyle !== "List" ? (
          <ReferralEntitiesListHybridView
            isLoadingList={isLoading}
            isLoadingMap={isLoadingMapData}
            isPartnerUser={isPartnerUser}
            showListMap={viewStyle === "ListMap"}
            partnerData={refEntityData}
            partnerLocationData={mapPartnerLocations}
            onShowPartnerServices={(partnerName, newServiceList) => {
              setServiceDialogServices(
                newServiceList.map((entry) => {
                  return { value: entry }
                })
              )
              setServiceDialogTitle(`Services offered by ${partnerName}`)
            }}
            onEditPartner={(partnerId) => {
              setEditPartnerId(partnerId)
              setShowPartnerForm(true)
            }}
            onPageChange={(e) => {
              onPageChange(e)
            }}
            //Mapping variables:
            mapHeight={mapHeight}
            mapCachedMap={mapCachedMap}
            mapFitToBounds={mapBoundingBox}
            mapDefaultCenteredLocation={mapCenteredPoint}
            mapDefaultFocusedLocation={{
              longitude: filters.longitude,
              latitude: filters.latitude,
            }}
            mapMarkerPoints={mapMarkerPoints}
            mapOnSetCachedMap={setMapCachedMap}
            mapOnSetDefaultFocusedLocation={(location) => {}}
            mapOnSetZoomRange={(range) => {
              if (
                !filters.allLocations &&
                (filters.distance < range.minRange ||
                  filters.distance > range.maxRange)
              ) {
                setFilters({ ...filters, distance: range.range })
              }
            }}
          />
        ) : null}
      </div>
    </div>
  )
}
